import './styles.scss';

import { Button, Col, Row } from 'react-bootstrap';
import {
  CreateUserAvailabilityBulkResponse,
  CreateUserAvailabilityInput,
  User,
  UserAvailability,
  UserAvailabilityTypeEnum,
} from 'models/gen/graphql';
import { DAYS_OF_THE_WEEK, TODAY } from '@/constants';
import { Datetime, Validation, dateWithCurrentTime, dateWithExistingTime, isDateRangeOverlap } from '../../utils';
import React, { useEffect, useMemo, useState } from 'react';
import { createUserAvailabilityValidator, handleCreateAvailability } from '../../api/services/users/createUserAvailabilityBulk';
import { handleApproveUserAvailability, handleDenyUserAvailability } from '../../api/services/users/approveUserAvailability';
import { handleUpdateAvailability, handleUpdateAvailabilitySeries } from '../../api/services/users/updateUserAvailabilityBulk';

import ConfirmationButton from '../ConfirmationButton';
import EditAvailabilityHeader from './EditAvailabilityHeader';
import EditPopover from 'components/EditPopover';
import FormField from 'components/FormField';
import Logger from '../../utils/logs';
import SelectActiveStatus from 'components/SelectActiveStatus';
import SelectAvailabilityType from '../SelectAvailabilityType';
import SelectFromEnum from '../SelectFromEnum';
import Tippy from '@tippyjs/react';
import TippyWhen from '@/components/TippyWhen';
import useConfirmation from '@/hooks/useConfirmation';
import useForm from '../../hooks/useForm';
import useValidation from '../../hooks/useValidation';

const log = Logger.of('EditAvailabilityPopover');

interface Props {
  title?: string;
  date: string;
  onSubmit?: any;
  onHide: any;
  onCancel: () => void;
  data: { availability?: UserAvailability; user: User };
  onDelete: any;
  children: any;
  setUserData?: (action: Partial<AvailabilityAction>) => void;
}
type State = Partial<UserAvailability> & CreateUserAvailabilityInput & { repeat: 0 | 1 | boolean; selectedDays?: number[] };
export type AvailabilityAction = {
  add: Partial<UserAvailability>[];
  update: Partial<UserAvailability>;
  updateSeries: Partial<UserAvailability>;
  approve: Partial<UserAvailability>;
  deny: Partial<UserAvailability>;
  delete: Partial<UserAvailability>;
  deleteSeries: Partial<UserAvailability>;
  revert: boolean;
};

const EditAvailabilityPopover = ({
  title,
  data,
  date,
  onDelete,
  onSubmit,
  onHide,
  onCancel,
  children,
  setUserData,
}: Props): JSX.Element => {
  const [show, setShow] = useState(undefined);
  // init state
  const [form, onChange, setForm] = useForm<State>(undefined);
  //const overlappingDays = useRef<number[]>([]);
  const [isValid, validity] = useValidation(createUserAvailabilityValidator, form);

  const { availability, user } = data;
  const hasSeries = !!availability?.repeatUntil;
  const mode = availability?.id ? 'edit' : 'create';
  const isRequest = mode === 'edit' && Validation.isNil(availability?.approved);

  const confirmMultipleCreate = useConfirmation({
    Header: {
      title: 'Create Multiple Availabilities',
    },
    Body: {
      message: 'You are about to create multiple availabilities. Are you sure you want to continue?',
    },
  });

  const setAvailabilityToForm = (): void => {
    const newDate = new Datetime().setDate(date);
    if (availability) {
      setForm({
        ...availability,
        repeat: !!availability?.repeatUntil,
        selectedDays: [newDate.day],
      });
    } else {
      setForm({
        startDate: newDate.dateInput,
        startTime: undefined,
        endDate: newDate.dateInput,
        endTime: undefined,
        dayOfWeek: newDate.day,
        availabilityType: '',
        repeat: 0,
        reason: '',
        userId: user?.id,
        selectedDays: [newDate.day],
      });
    }
  };

  useEffect((): void => {
    setAvailabilityToForm();
  }, [data]);

  useEffect((): (() => void) => {
    setShow(undefined);
    return (): void => {
      setShow(undefined);
    };
  }, []);

  const handleHide = (): void => {
    setShow(false);
    onHide();
    setAvailabilityToForm();
    //overlappingDays.current = [];
  };
  const handleCancel = (): void => {
    setShow(false);
    onCancel();
    setAvailabilityToForm();
  };

  // handler func
  const handleSubmit = async (): Promise<void> => {
    if (onSubmit) {
      await onSubmit(form);
      return handleHide();
    }
    const selectedDates = form?.repeat
      ? (form?.selectedDays || []).map((day: number): string => new Datetime(date).startOf('week').add(day, 'days').date)
      : null;
    if (mode === 'edit') {
      const updateUserAvailabilityPayload: UserAvailability = {
        startDate: form?.startDate,
        startTime: Validation.isDate(form?.startTime) ? new Datetime(form?.startTime).fullTime : form?.startTime,
        endDate: form?.endDate,
        endTime: Validation.isDate(form?.endTime) ? new Datetime(form?.endTime).fullTime : form?.endTime,
        repeatUntil: form?.repeatUntil,
        dayOfWeek: form?.dayOfWeek,
        type: form?.type,
        reason: form?.reason,
        userId: form?.userId,
        id: form?.id,
        approved: form?.approved,
      };
      // call update
      setUserData({ update: updateUserAvailabilityPayload });
      const res = await handleUpdateAvailability(updateUserAvailabilityPayload, availability);
      if (!res) setUserData({ revert: true });
    } else {
      // call create
      if (selectedDates?.length > 1) await confirmMultipleCreate();
      const createUserAvailabilityPayload: CreateUserAvailabilityInput = {
        startDate: form.startDate,
        startTime: Validation.isDate(form.startTime) ? new Datetime(form.startTime).fullTime : form.startTime,
        endDate: form.endDate,
        endTime: Validation.isDate(form.endTime) ? new Datetime(form.endTime).fullTime : form.endTime,
        repeatUntil: !!form?.repeat && form.repeatUntil ? form.repeatUntil : null,
        dayOfWeek: form.dayOfWeek,
        availabilityType: form.availabilityType,
        reason: form.reason,
        userId: form.userId,
      };
      const res = await handleCreateAvailability(createUserAvailabilityPayload, selectedDates);
      if (res) {
        const newAvailability = (res as CreateUserAvailabilityBulkResponse).output.map((item) => item?.node);
        setUserData({ add: newAvailability });
      }
    }
    handleHide();
  };
  const handleDenyAvailability = async (): Promise<void> => {
    try {
      if (!isRequest) throw new Error('Unable to deny non availability requests.');
      setUserData({ deny: availability });
      const res = await handleDenyUserAvailability([availability?.id]);
      if (!res) setUserData({ revert: true });
    } catch (err) {
      const message = err.message || err;
      log.error('handleDenyAvailability', message).notify({ title: 'Deny User Availability', message });
    } finally {
      handleHide();
    }
  };
  const handleApproveAvailability = async (): Promise<void> => {
    try {
      if (!isRequest) throw new Error('Unable to approve non availability requests.');
      setUserData({ approve: availability });
      const res = await handleApproveUserAvailability([availability?.id]);
      if (!res) setUserData({ revert: true });
    } catch (err) {
      const message = err.message || err;
      log.error('handleApproveAvailability', message).notify({ title: 'Approve User Availability', message });
    } finally {
      handleHide();
    }
  };
  const handleUpdateSeries = async (): Promise<void> => {
    try {
      const updateSeriesPayload: UserAvailability = {
        startDate: form?.startDate,
        startTime: Validation.isDate(form?.startTime) ? new Datetime(form?.startTime).fullTime : form?.startTime,
        endDate: form?.endDate,
        endTime: Validation.isDate(form?.endTime) ? new Datetime(form?.endTime).fullTime : form?.endTime,
        repeatUntil: form?.repeatUntil,
        dayOfWeek: form?.dayOfWeek,
        type: form?.type,
        reason: form?.reason,
        userId: form?.userId,
        id: form?.id,
        approved: form?.approved,
      };
      if (onSubmit) await onSubmit(updateSeriesPayload);
      else if (mode === 'edit') {
        setUserData({ updateSeries: updateSeriesPayload });
        const res = await handleUpdateAvailabilitySeries(updateSeriesPayload, availability);
        if (!res) setUserData({ revert: true });
      }
    } catch (err) {
      log.error('handleUpdateSeries', err.message || err);
    } finally {
      handleHide();
    }
  };
  const handleDelete = (): void => {
    handleCancel();
    onDelete(availability?.id);
  };

  const toggleSelectedDays =
    (day: number): (() => void) =>
    (): void => {
      if (form?.dayOfWeek === day) return;
      setForm((current: State) => {
        const selectedDays = (current.selectedDays || []).includes(day)
          ? current?.selectedDays.filter((d: number): boolean => d !== day)
          : [...(current.selectedDays || []), day];
        return {
          ...current,
          selectedDays: selectedDays.slice().sort((a: number, b: number): number => a - b),
        };
      });
    };

  const handleDateTimeChange = (): number[] => {
    if (mode === 'edit' || !user?.availability) return;

    // 1) Convert user.availability to [from, to]
    const existingRanges = (user?.availability || []).map(({ startDate, endDate, startTime, endTime }) => {
      const from = new Datetime(startDate).setTime(startTime).datetime;
      const to = new Datetime(endDate).setTime(endTime).datetime;
      return [from, to] as [string, string];
    });

    // 2) Convert form.selectedDays (array of dayOfWeek: 0-6) to [from, to]
    const startOfThisWeek = new Datetime(date).startOf('week');
    const newRanges = (form?.selectedDays || []).map((dayOfWeek) => {
      if (new Datetime(TODAY).day > dayOfWeek) return ['', ''] as [string, string];
      const from = new Datetime(form.startTime).time;
      const to = new Datetime(form.endTime).time;
      const fromDate = startOfThisWeek.clone().add(dayOfWeek, 'days').setTime(from).datetime;
      const toDate = startOfThisWeek.clone().add(dayOfWeek, 'days').setTime(to).datetime;
      return [fromDate, toDate] as [string, string];
    });

    // 3) For each newly selected day range, check if it overlaps
    //    with any existing range. If so, add that dayOfWeek to 'overlaps'.
    const overlaps: number[] = [];
    (form?.selectedDays || []).forEach((dayOfWeek, i) => {
      if (existingRanges.some((existing) => isDateRangeOverlap(existing, newRanges[i]))) {
        overlaps.push(dayOfWeek);
      }
    });

    // 4) Deduplicate and store
    return Array.from(new Set(overlaps));
  };

  const overlappingDays = useMemo(() => {
    return handleDateTimeChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.startDate, form?.endDate, form?.selectedDays]);

  const currentDateAndTime = dateWithCurrentTime(TODAY);
  const selectedDateAndTime = dateWithExistingTime(form?.startDate, new Datetime(form?.startTime).toString());
  const isPastDate = selectedDateAndTime.isBefore(currentDateAndTime.toString(), 'day');

  return (
    <EditPopover
      show={show}
      target={children}
      title={title || ''}
      size="xl"
      onHide={handleCancel}
      onSubmit={undefined}
      loading={false}
      name="editAvailability"
      options={{
        Header: <EditAvailabilityHeader data={user} />,
      }}
    >
      <Row className="EditAvailabilityPopover ">
        <Col>
          {/* Time Inputs */}
          <Row xs={2}>
            <Col>
              <FormField
                label="START TIME:"
                type="time"
                name="startTime"
                onChange={onChange.time}
                value={Validation.isDate(form?.startTime) ? new Datetime(form?.startTime).fullTime : form?.startTime}
                valid={validity?.startTime?.valid}
                disabled={isRequest}
                placeholder="--:--"
                condensed
              />
            </Col>
            <Col>
              <FormField
                label="END TIME:"
                type="time"
                name="endTime"
                onChange={onChange.time}
                value={Validation.isDate(form?.endTime) ? new Datetime(form?.endTime).fullTime : form?.endTime}
                valid={validity?.endTime?.valid}
                disabled={isRequest}
                placeholder="--:--"
                condensed
              />
            </Col>
          </Row>
          {/* Repeat Inputs */}
          <Row xs={2}>
            <Col>
              <FormField
                label="REPEAT:"
                name="repeat"
                onChange={onChange}
                value={form?.repeat}
                disabled={isRequest || mode === 'edit'}
                placeholder={'Repeat?'}
                statusType="repeat"
                options={{ input: { as: SelectActiveStatus } }}
                condensed
              />
            </Col>
            <Col>
              <FormField
                label="REPEAT UNTIL DATE:"
                type="date"
                name="repeatUntil"
                onChange={onChange.date}
                disabled={!form?.repeat || isRequest || mode === 'edit'}
                value={form?.repeatUntil || ''}
                valid={validity?.repeatUntil?.valid}
                condensed
              />
            </Col>
          </Row>
          {/* Multi Day Selection Buttons */}
          {!!form?.repeat && mode === 'create' && (
            <Row className="px-5">
              {DAYS_OF_THE_WEEK.map((day: string, i: number) => (
                <TippyWhen
                  key={day}
                  isTrue={new Datetime(date).startOf('week').add(i, 'days').isBefore(TODAY)}
                  options={{
                    content: 'Availability for this day will not be created in the past',
                  }}
                >
                  <Col key={day}>
                    <Button
                      variant={(form?.selectedDays || [])?.includes(i) ? 'secondary-subtle' : 'outline-primary'}
                      className="Weekday-Button"
                      onClick={() => {
                        toggleSelectedDays(i)();
                      }}
                    >
                      {overlappingDays.includes(i) && (
                        <Tippy content="This day has existing availability">
                          <i className="sv sv-warning" />
                        </Tippy>
                      )}
                      <div>{day?.substring(0, 3).toUpperCase()}</div>
                    </Button>
                  </Col>
                </TippyWhen>
              ))}
            </Row>
          )}
          <Row xs={2}>
            {/* Request Type Input */}
            <Col>
              {mode === 'edit' ? (
                <FormField
                  label="REQUEST TYPE:"
                  name="type"
                  onChange={(event): void => {
                    return event?.target?.value ? onChange(event) : undefined;
                  }}
                  disabled={true}
                  value={form?.type || ''}
                  valid={validity?.type?.valid}
                  source={UserAvailabilityTypeEnum}
                  getLabel={(key: string) => key.toUpperCase()}
                  placeholder="Request Type"
                  options={{ input: { as: SelectFromEnum } }}
                  condensed
                />
              ) : (
                <FormField
                  label="REQUEST TYPE:"
                  name="availabilityType"
                  onChange={(event): void => {
                    return event?.target?.value ? onChange(event) : undefined;
                  }}
                  valid={!!form?.availabilityType}
                  value={form?.availabilityType || ''}
                  placeholder="Request Type"
                  options={{ input: { as: SelectAvailabilityType } }}
                  condensed
                />
              )}
            </Col>
            {/* Reason Input */}
            <Col>
              <FormField
                label="REASON:"
                name="reason"
                onChange={onChange}
                valid={validity?.type?.valid}
                value={form?.reason || ''}
                disabled={isRequest || (mode === 'edit' && availability?.approved === 1)}
                placeholder="Reason"
                maxLength={128}
                condensed
              />
            </Col>
          </Row>
          {!isRequest && (
            <>
              {/* Buttons */}
              <Row xs={2} className="flex-wrap gy-3">
                {/* Save Button */}
                <Col xs={mode === 'create' ? 12 : undefined}>
                  <TippyWhen
                    isTrue={isPastDate}
                    options={{
                      content: 'Changes cannot be made to past availability',
                    }}
                  >
                    <span>
                      <Button
                        variant="primary"
                        className="shadow w-100"
                        name="SUBMIT"
                        onClick={handleSubmit}
                        disabled={form?.repeat ? !isValid : !isValid || isPastDate}
                      >
                        Save
                      </Button>
                    </span>
                  </TippyWhen>
                </Col>
                {/* Edit Series Button */}
                {hasSeries && (
                  <Col>
                    <Button
                      variant="primary"
                      className="shadow w-100 "
                      name="UPDATE_SERIES"
                      onClick={handleUpdateSeries}
                      disabled={!isValid}
                    >
                      Save Series
                    </Button>
                  </Col>
                )}
                {/* Delete Button */}
                {mode === 'edit' && (
                  <Col xs={hasSeries ? 12 : { span: 6, order: 'first' }}>
                    <Button variant="red" className="shadow w-100 " name="DELETE" onClick={handleDelete}>
                      Delete
                    </Button>
                  </Col>
                )}
              </Row>
            </>
          )}
          {isRequest && (
            <Row xs={1} className="flex-wrap gy-3">
              <Col>
                <ConfirmationButton variant="red" className="shadow w-100" name="DENIED" onConfirm={handleDenyAvailability}>
                  Denied
                </ConfirmationButton>
              </Col>
              {/* Approve Button */}
              <Col>
                <Button variant="success" className="shadow w-100" name="APPROVE" onClick={handleApproveAvailability}>
                  Approved
                </Button>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </EditPopover>
  );
};

export default React.memo(EditAvailabilityPopover);
